import { router } from 'expo-router'
import { useCallback } from 'react'
import { View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import InitialsAvatar from '@/components/navigation/InitialsAvatar'
import StyledSearchTitle from '@/components/search/SearchStyledTitle'
import UniButton from '@/components/unistyles/UniButton'
import UniImage from '@/components/unistyles/UniImage'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import useUserRoles from '@/hooks/useUserRoles'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'
import { stripMarkers } from '@/utils/strings'

interface Props {
  id: string
  name: string
  thumbUri?: string | null
  fosterCount: number
}

const avatarSize = 50

const UserSearchItem = ({ id, name, thumbUri, fosterCount }: Props) => {
  const { theme } = useUnistyles()
  const { isUserAdmin } = useUserRoles()

  const advancedFilters = useAppStore.use.advancedFilters()

  const setBasicFilters = useAppStore.use.setBasicFilters()
  const setAdvancedFilters = useAppStore.use.setAdvancedFilters()

  const handleViewFosters = useCallback(() => {
    setBasicFilters({
      isActive: true,
      isDirty: true,
      filters: {
        currentlyFostered: true,
        includeDeceased: false,
        readyForAdoption: false,
        species: undefined,
        viewAll: true,
        volunteerId: id,
      },
    })
    setAdvancedFilters({
      ...advancedFilters,
      isActive: false,
    })

    router.push(buildOrgPath('/fosters'))
  }, [advancedFilters, id, setAdvancedFilters, setBasicFilters])

  const content = (
    <>
      <View style={styles.topRow}>
        <View style={styles.textContainer}>
          <StyledSearchTitle id={id} text={name} variant="labelLarge" />
          <StyledSearchTitle
            id={id}
            style={{ color: theme.colors.secondary }}
            text={`${fosterCount} current foster${fosterCount === 1 ? '' : 's'}`}
            variant="labelSmall"
          />
        </View>
        {thumbUri ? (
          <UniImage
            accessibilityIgnoresInvertColors={true}
            cachePolicy={'disk'}
            contentFit="cover"
            source={thumbUri}
            style={styles.avatar}
            transition={0}
          />
        ) : (
          <InitialsAvatar
            size={avatarSize}
            string={stripMarkers(name)}
            style={styles.avatar}
          />
        )}
      </View>
      {isUserAdmin && (
        <View style={styles.buttonContainer}>
          <UniButton
            mode="outlined"
            onPress={handleViewFosters}
            style={styles.button}
          >
            View Fosters
          </UniButton>
          <UniButton
            mode="outlined"
            onPress={() => router.push(buildOrgPath(`/users/${id}`))}
            style={styles.button}
          >
            View Profile
          </UniButton>
        </View>
      )}
    </>
  )

  if (isUserAdmin) {
    return <View style={styles.container}>{content}</View>
  }

  return (
    <UniTouchableRipple
      accessibilityRole="button"
      borderless={true}
      onPress={handleViewFosters}
      style={styles.container}
      testID={`user-search-item-${name.replace(/\s+/g, '-').replaceAll('!', '').toLocaleLowerCase()}-${id}`}
    >
      {content}
    </UniTouchableRipple>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    avatar: {
      borderColor: theme.colors.cardBorder,
      borderRadius: avatarSize / 2,
      borderWidth: 2,
      height: avatarSize,
      width: avatarSize,
    },
    button: {
      borderRadius: theme.tokens.containerBorderRadius,
      flex: 1,
    },
    buttonContainer: {
      flexDirection: 'row',
      gap: theme.tokens.spacing[2],
      paddingBottom: theme.tokens.spacing[3],
      paddingHorizontal: theme.tokens.spacing[3],
    },
    container: {
      borderRadius: theme.tokens.containerBorderRadius,
      margin: theme.tokens.spacing[2],
    },
    textContainer: {
      flex: 1,
      justifyContent: 'center',
    },
    topRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.tokens.spacing[3],
      padding: theme.tokens.spacing[3],
    },
  }
})

export default UserSearchItem
